import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import stilePulsanti from "../interfaccia/stilePulsanti";
import apiUrl from "../Apiurl";
import RiquadrinoOrdine from "./AreaOrdiniComponenti/OrdineComponenti/RiquadrinoOrdine";
import beep from "../../components/files/sounds/beep-6-96243.mp3";
import errorSound from "../../components/files/sounds/error-call-to-attention-129258.mp3";

function AreaSparaTrovati({ utente }) {
  const { inputClassesConTasto, buttonClassesGreen, buttonClassesRed } =
    stilePulsanti;

  const { user, getAccessTokenSilently } = useAuth0();
  const [token, setToken] = useState("");

  const [magazzini, setMagazzini] = useState([]);
  const [magazzinoDesc, setMagazzinoDesc] = useState("");
  const [input, setInput] = useState("");
  const [idSparati, setIdSparati] = useState([]);
  const [errori, setErrori] = useState({});
  const [daSelezionare, setDaSelezionare] = useState([]);
  const [selezioni, setSelezioni] = useState({});
  const [messaggio, setMessaggio] = useState("");
  const [messaggioErrore, setMessaggioErrore] = useState("");
  const [controlloOk, setControlloOk] = useState(false);
  const [ordiniRicerca, setOrdiniRicerca] = useState([]);
  const [loadingOrdiniRicerca, setLoadingOrdiniRicerca] = useState(false);

  const inputRef = useRef(null);

  const suonoConferma = new Audio(beep);
  const suonoErrore = new Audio(errorSound);

  const opzioniById = useMemo(() => {
    const map = {};
    daSelezionare.forEach((item) => {
      map[item.idPS] = item;
    });
    return map;
  }, [daSelezionare]);

  const magazzinoSelezionato = useMemo(() => {
    if (!magazzinoDesc) {
      return null;
    }
    const trovato =
      magazzini.find((mag) => mag.descPartenzaDa === magazzinoDesc) ||
      magazzini.find((mag) => mag.nome === magazzinoDesc);
    return trovato || { descPartenzaDa: magazzinoDesc, nome: magazzinoDesc };
  }, [magazzini, magazzinoDesc]);

  const conteggioId = useMemo(() => {
    const counts = {};
    idSparati.forEach((id) => {
      counts[id] = (counts[id] || 0) + 1;
    });
    return counts;
  }, [idSparati]);

  const duplicati = useMemo(
    () => Object.keys(conteggioId).filter((id) => conteggioId[id] > 1),
    [conteggioId]
  );

  const ordiniRicercaIdsSet = useMemo(() => {
    const set = new Set();
    ordiniRicerca.forEach((ordine) => {
      const idPS = parseInt(ordine?.N_rif_PrestaShop, 10);
      if (idPS) {
        set.add(idPS);
      }
    });
    return set;
  }, [ordiniRicerca]);

  const ordiniRicercaDaSparare = useMemo(() => {
    return ordiniRicerca.filter((ordine) => {
      const idPS = parseInt(ordine?.N_rif_PrestaShop, 10);
      return !idPS || !idSparati.includes(idPS);
    });
  }, [ordiniRicerca, idSparati]);

  const ordiniRicercaTrovati = useMemo(() => {
    return ordiniRicerca.filter((ordine) => {
      const idPS = parseInt(ordine?.N_rif_PrestaShop, 10);
      return idPS && idSparati.includes(idPS);
    });
  }, [ordiniRicerca, idSparati]);

  const fetchMagazzini = async () => {
    const params = new URLSearchParams({
      tabella: "Magazzini",
      ordinamento: "ORDER BY nome ASC",
    });
    const response = await axios.get(
      apiUrl + "/anagrafiche/generica?" + params.toString()
    );
    setMagazzini(response.data || []);
  };

  useEffect(() => {
    fetchMagazzini();
  }, []);

  useEffect(() => {
    setControlloOk(false);
    setMessaggio("");
  }, [idSparati, duplicati.length, magazzinoDesc, ordiniRicerca.length]);

  useEffect(() => {
    const getUserMetadata = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        setToken(accessToken);
      } catch (e) {
        setToken("");
      }
    };
    getUserMetadata();
  }, [getAccessTokenSilently, user?.sub]);

  useEffect(() => {
    const descPartenzaDa = magazzinoSelezionato?.descPartenzaDa;
    if (!descPartenzaDa) {
      setOrdiniRicerca([]);
      return;
    }
    setLoadingOrdiniRicerca(true);
    fetch(
      apiUrl +
        "/logistica/sparaOrdini/ordiniRicercaMag/" +
        encodeURIComponent(descPartenzaDa)
    )
      .then((resp) => resp.json())
      .then((data) => {
        setOrdiniRicerca(Array.isArray(data) ? data : []);
      })
      .catch(() => setOrdiniRicerca([]))
      .finally(() => setLoadingOrdiniRicerca(false));
  }, [magazzinoSelezionato]);

  const barcodeToOrder = function (barcode) {
    const raw = String(barcode || "").trim();
    if (!raw) {
      return null;
    }
    const slice = raw.slice(-7);
    const parsed = parseInt(slice, 10);
    if (Number.isNaN(parsed)) {
      return null;
    }
    return parsed;
  };

  const handleInput = function (evt) {
    setInput(evt.target.value);
  };

  const handleKeyDown = function (event) {
    if (event.key === "Enter") {
      event.preventDefault();
      setMessaggio("");
      setMessaggioErrore("");

      if (!magazzinoSelezionato) {
        setMessaggioErrore("Seleziona prima un magazzino.");
        suonoErrore.play();
        return;
      }

      const ordineDaTrasmettere = barcodeToOrder(input);
      if (!ordineDaTrasmettere) {
        setMessaggioErrore("Barcode non valido.");
        suonoErrore.play();
        return;
      }

      setIdSparati((prev) => {
        const isDuplicato = prev.includes(ordineDaTrasmettere);
        if (isDuplicato) {
          setMessaggioErrore("Doppione rilevato: " + ordineDaTrasmettere);
          suonoErrore.play();
        } else {
          suonoConferma.play();
        }
        return [...prev, ordineDaTrasmettere];
      });
      setInput("");
      inputRef.current?.focus();
    }
  };

  const handleRimuovi = function (idPS, indexToRemove) {
    setIdSparati((prev) => {
      const index =
        typeof indexToRemove === "number" ? indexToRemove : prev.indexOf(idPS);
      if (index === -1 || index >= prev.length) {
        return prev;
      }
      const next = [...prev];
      next.splice(index, 1);
      const stillPresent = next.includes(idPS);
      if (!stillPresent) {
        setErrori((prevErr) => {
          const nextErr = { ...prevErr };
          delete nextErr[idPS];
          return nextErr;
        });
        setSelezioni((prevSel) => {
          const nextSel = { ...prevSel };
          delete nextSel[idPS];
          return nextSel;
        });
        setDaSelezionare((prevSel) =>
          prevSel.filter((item) => item.idPS !== idPS)
        );
      }
      return next;
    });
  };

  const descriviErrore = function (errore) {
    if (!errore) return null;
    switch (errore.motivo) {
      case "nessuna_spedizione":
        return "Nessuna spedizione trovata con questo ID PS.";
      case "partenza_diversa":
        return `Spedizione trovata ma con magazzino di partenza diverso (atteso: ${
          errore.partenzaAttesa || "?"
        }).`;
      case "id_non_valido":
        return "ID non valido.";
      case "selezione_non_valida":
        return "Selezione non valida, scegli una spedizione.";
      case "stato_non_valido":
        return `Stato attuale della spedizione: ${
          errore.statoOrdine || "N/D"
        }.`;
      default:
        return "Errore in elaborazione.";
    }
  };

  const handleSelezioneSpedizione = function (idPS, value) {
    setSelezioni((prev) => ({
      ...prev,
      [idPS]: value,
    }));
  };

  const handleControlla = async function () {
    if (!magazzinoSelezionato || idSparati.length === 0) {
      return;
    }
    setMessaggio("");
    setMessaggioErrore("");

    if (duplicati.length > 0) {
      setMessaggioErrore("Rimuovi i doppioni prima di continuare.");
      setControlloOk(false);
      return;
    }

    try {
      const response = await fetch(
        apiUrl + "/logistica/sparaOrdini/sparaTrovati",
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ids: idSparati,
            magazzino: magazzinoSelezionato,
            selections: selezioni,
            userName: utente?.username || "",
            dryRun: true,
          }),
        }
      );

      const data = await response.json();

      const erroriMap = {};
      (data?.errori || []).forEach((err) => {
        erroriMap[err.idPS] = err;
      });
      setErrori(erroriMap);
      setDaSelezionare(data?.daSelezionare || []);

      const hasErroriLocale =
        Object.keys(erroriMap).length > 0 ||
        (data?.daSelezionare || []).length > 0;

      if (hasErroriLocale) {
        setMessaggioErrore(
          "Ci sono non conformità: correggi e riprova il controllo."
        );
        setControlloOk(false);
      } else {
        setMessaggio("Controllo ok. Puoi confermare.");
        setControlloOk(true);
      }
    } catch (err) {
      setMessaggioErrore("Errore durante il controllo degli ID.");
      setControlloOk(false);
    }
  };

  const handleConferma = async function () {
    if (!magazzinoSelezionato || idSparati.length === 0) {
      return;
    }
    setMessaggio("");
    setMessaggioErrore("");

    try {
      const response = await fetch(
        apiUrl + "/logistica/sparaOrdini/sparaTrovati",
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ids: idSparati,
            magazzino: magazzinoSelezionato,
            selections: selezioni,
            userName: utente?.username || "",
          }),
        }
      );

      const data = await response.json();
      const aggiornatiIds = (data?.aggiornati || []).map((item) => item.idPS);

      setIdSparati((prev) => prev.filter((id) => !aggiornatiIds.includes(id)));

      const erroriMap = {};
      (data?.errori || []).forEach((err) => {
        erroriMap[err.idPS] = err;
      });
      setErrori(erroriMap);

      setDaSelezionare(data?.daSelezionare || []);
      setSelezioni((prev) => {
        const next = { ...prev };
        aggiornatiIds.forEach((id) => delete next[id]);
        return next;
      });

      if (aggiornatiIds.length > 0) {
        setMessaggio("Aggiornati " + aggiornatiIds.length + " ordini.");
        setControlloOk(false);
      }
      if ((data?.errori || []).length > 0 || data?.daSelezionare?.length > 0) {
        setMessaggioErrore(
          "Ci sono non conformità: correggi e riprova il controllo."
        );
        setControlloOk(false);
      }
    } catch (err) {
      setMessaggioErrore("Errore durante la conferma degli ID.");
    }
  };

  const hasErrori = Object.keys(errori).length > 0;
  const hasDuplicati = duplicati.length > 0;
  const hasSelezioniIncerte = daSelezionare.some(
    (item) => !selezioni[item.idPS]
  );
  const confermaDisabilitata =
    !magazzinoSelezionato ||
    idSparati.length === 0 ||
    hasErrori ||
    hasSelezioniIncerte ||
    hasDuplicati ||
    !controlloOk;

  return (
    <div className="areaComposizione fr">
      <div className="areaComposizione colonnaTuttaPagina sfondoBiancoTema br4 br--bottom shadow-4 pa2">
        <div className="tc">
          <h2 className="">
            {`SPARA TROVATI - ${
              magazzinoSelezionato?.nome || "SELEZIONA MAGAZZINO"
            }`}
          </h2>
          <h3 className="">Seleziona il magazzino</h3>
          <select
            className="db ba bordoColScrittaDef-10 pa2 br2 mb2 center"
            value={magazzinoDesc}
            onChange={(event) => {
              const value = event.target.value;
              setMagazzinoDesc(value);
              setErrori({});
              setDaSelezionare([]);
              setSelezioni({});
            }}
          >
            <option value="">Seleziona magazzino...</option>
            {magazzini.map((mag) => (
              <option
                key={mag.descPartenzaDa || mag.nome}
                value={mag.descPartenzaDa || mag.nome}
              >
                {mag.nome}
              </option>
            ))}
          </select>
          <h3>Assicurati che il cursore lampeggi e spara il barcode</h3>
          <input
            autoFocus
            ref={inputRef}
            type="text"
            id="input"
            name="input"
            value={input}
            autoComplete="off"
            onChange={handleInput}
            className={inputClassesConTasto}
            onKeyDown={handleKeyDown}
            disabled={!magazzinoSelezionato}
          />
          <div className="tc mt2">
            <button
              className={buttonClassesGreen + " mr2"}
              disabled={!magazzinoSelezionato || idSparati.length === 0}
              onClick={handleControlla}
            >
              CONTROLLA
            </button>
            <button
              className={
                confermaDisabilitata
                  ? "bg-light-gray gray ba b--light-gray br3 ph3 pv2 dib cursor-not-allowed"
                  : buttonClassesGreen
              }
              disabled={confermaDisabilitata}
              onClick={handleConferma}
            >
              CONFERMA TROVATI
            </button>
          </div>
          {messaggio && <div className="mt2">{messaggio}</div>}
          {messaggioErrore && <div className="mt2 red">{messaggioErrore}</div>}
        </div>

        <div className="mt3 flex flex-wrap">
          <div className="w-50-l w-100 pr3">
            <h3 className="tc">{`Ordini in RICERCA ${
              magazzinoSelezionato?.nome
                ? "su " + magazzinoSelezionato.nome
                : ""
            } (${ordiniRicerca.length})`}</h3>
            <div
              className="sfondoColChiaro br2 pa3 flex flex-wrap"
              style={{ maxHeight: "70vh", overflowY: "auto" }}
            >
              {loadingOrdiniRicerca && (
                <div className="tc gray">Caricamento…</div>
              )}
              {!loadingOrdiniRicerca &&
                ordiniRicercaDaSparare.length === 0 &&
                ordiniRicercaTrovati.length === 0 && (
                  <div className="tc">Nessun ordine in ricerca.</div>
                )}
              {!loadingOrdiniRicerca && (
                <div className="flex w-100">
                  <div className="w-50 pr2">
                    <div className="tc b mb2">DA SPARARE</div>
                    {ordiniRicercaDaSparare.map((ordine) => {
                      const idPS = parseInt(ordine?.N_rif_PrestaShop, 10);
                      return (
                        <div
                          key={ordine["ID Spedizione"] || idPS || Math.random()}
                          className="pa2"
                        >
                          <RiquadrinoOrdine
                            ordine={ordine}
                            mostraArticoli
                            token={token || "1"}
                            mostraStato={false}
                            mostraDate={false}
                            mostraMagazzino={false}
                            mostraTipo={true}
                            mostraCliente={true}
                            mostraIdPS={true}
                            classeSfondo={"grey"}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className="w-50 pl2">
                    <div className="tc b mb2">TROVATI</div>
                    {ordiniRicercaTrovati.map((ordine) => {
                      const idPS = parseInt(ordine?.N_rif_PrestaShop, 10);
                      return (
                        <div
                          key={ordine["ID Spedizione"] || idPS || Math.random()}
                          className="pa2"
                        >
                          <RiquadrinoOrdine
                            ordine={ordine}
                            mostraArticoli
                            token={token || "1"}
                            mostraStato={false}
                            mostraDate={false}
                            mostraMagazzino={false}
                            mostraTipo={true}
                            mostraCliente={true}
                            mostraIdPS={true}
                            classeSfondo={"bg-washed-green"}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="w-50-l w-100 pl3">
            <h3 className="tc">Totale sparati: {idSparati.length}</h3>
            {duplicati.length > 0 && (
              <div className="tc mb2 red">
                Doppioni:{" "}
                {duplicati
                  .map((id) => `${id} (x${conteggioId[id]})`)
                  .join(", ")}
              </div>
            )}

            {idSparati.length === 0 && (
              <div className="tc">Nessun ID in lista.</div>
            )}

            <div className="flex flex-wrap">
              {idSparati.map((id, index) => {
                const errore = errori[id];
                const opzioni = opzioniById[id];
                const isDuplicato = conteggioId[id] > 1;
                const nonPrevisto = !ordiniRicercaIdsSet.has(id);
                return (
                  <div
                    key={`${id}-${index}`}
                    className={
                      "mw5 flex items-center justify-between ba br2 pa2 mb2 mr2 " +
                      (isDuplicato ? "bg-washed-red dark-red" : "")
                    }
                  >
                    <div className="mr2">
                      <span className="b">
                        {id} {nonPrevisto && <span className="ml2">?</span>}
                      </span>
                      {errore && (
                        <div className="mt1 red">
                          {descriviErrore(errore)}
                          {errore.spedizioni && (
                            <div>
                              {"("}
                              {errore.spedizioni
                                .map(
                                  (sped) =>
                                    `${sped.idSped} - ${sped.partenzaDa} - ${sped.statoOrdine}`
                                )
                                .join(" | ")}
                              {")"}
                            </div>
                          )}
                        </div>
                      )}
                      {opzioni && (
                        <div className="mt1">
                          <div className="mb1">Seleziona la spedizione:</div>
                          <select
                            className="db ba bordoColScrittaDef-10 pa2 br2"
                            value={selezioni[id] || ""}
                            onChange={(event) =>
                              handleSelezioneSpedizione(id, event.target.value)
                            }
                          >
                            <option value="">Scegli...</option>
                            {opzioni.opzioni.map((sped) => (
                              <option key={sped.idSped} value={sped.idSped}>
                                {sped.idSped} - {sped.partenzaDa} -{" "}
                                {sped.statoOrdine}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                    <button
                      className={buttonClassesRed}
                      onClick={() => handleRimuovi(id, index)}
                    >
                      Rimuovi
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AreaSparaTrovati;
